/**
 * OfferContractorDialog — Phase 1 canonical send-offer dialog.
 *
 * Shown before any offer leaves the dispatcher's surface. Surfaces:
 *   - installer (company name)
 *   - job id + scope summary
 *   - read-only compensation breakdown from the review workspace
 *   - TTL display
 *
 * On confirm: routes through useMarketplaceOffers().create({ ... }) using the
 * already-reviewed compensation snapshot from the workspace.
 *
 * NEVER exposes Job.financials.contractValue or any customer-facing
 * economics. Pure installer-compensation surface.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Send, Clock, Calendar as CalendarIcon } from "lucide-react";
import type { Contractor, Job } from "@/data/types";
import { DEFAULT_OFFER_TTL_MINUTES } from "@/data/contractor/marketplaceOffer";
import { displayIdentity } from "@/lib/uuid";
import { getOfferCompensationDefaults } from "@/data/contractor/offerCompensationDefaults";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import type { CompensationDraftPreview } from "@/components/installerOffers/CompensationBuilder";

const INSTALLER_OFFERS_TIME_ZONE = "America/New_York";

export interface OfferContractorDialogConfirm {
  payoutAmountOverride?: number;
  reason?: string;
  /** Resolved TTL in minutes (preset or custom date/time → minutes from now). */
  ttlMinutes?: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job;
  contractor: Contractor;
  /** Defaults to DEFAULT_OFFER_TTL_MINUTES. */
  ttlMinutes?: number;
  /**
   * Optional baseline override. When provided, this value replaces
   * `job.payoutAmount` as the dialog's seeded compensation + baseline
   * comparison. Used by JobOfferReviewWorkspace to surface the live
   * calculated offer (e.g. 15% of net) instead of any stale saved payout.
   */
  baselineOverride?: number;
  /**
   * ISO timestamp the customer contract was executed (sold). Shown as
   * "Sold N days ago" so the dispatcher sees how long the job has been
   * waiting for installer assignment.
   */
  contractExecutedAt?: string;
  compensationSummary?: Pick<
    CompensationDraftPreview,
    | "incentiveTotal"
    | "effectiveOfferTotal"
    | "payoutOptionLabel"
    | "contractValue"
    | "financeFeePct"
    | "financeFeeAmount"
    | "netContract"
    | "canViewContract"
  > & {
    basePayout: number;
  };
  onConfirm: (data: OfferContractorDialogConfirm) => void;
}


const PRESET_TTLS: Array<{ minutes: number; label: string }> = [
  { minutes: 60, label: "1h" },
  { minutes: 120, label: "2h" },
  { minutes: 240, label: "4h" },
  { minutes: 480, label: "8h" },
  { minutes: 1440, label: "24h" },
  { minutes: 2880, label: "48h" },
];

type ExpiryMode = "preset" | "custom";

/** Format an ISO datetime as the local value an `<input type="datetime-local">` expects. */
function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function formatTtlLabel(minutes: number): string {
  if (minutes <= 0) return "expired";
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) {
    const hrs = Math.round((minutes / 60) * 10) / 10;
    return `${hrs}h`;
  }
  const days = Math.round((minutes / 1440) * 10) / 10;
  return `${days}d`;
}

function formatAge(fromIso: string, now: Date): string {
  const ms = now.getTime() - new Date(fromIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "today";
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `${days} day${days === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(ms / 3_600_000);
  if (hrs >= 1) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const mins = Math.max(1, Math.floor(ms / 60_000));
  return `${mins} min${mins === 1 ? "" : "s"} ago`;
}

export function OfferContractorDialog({
  open,
  onOpenChange,
  job,
  contractor,
  ttlMinutes: ttlDefault = DEFAULT_OFFER_TTL_MINUTES,
  baselineOverride,
  contractExecutedAt,
  compensationSummary,
  onConfirm,
}: Props) {
  const fin = useFinancialPermissions();
  const baseline = compensationSummary?.basePayout
    ?? (typeof baselineOverride === "number" && baselineOverride >= 0
      ? Math.round(baselineOverride)
      : (job.payoutAmount ?? 0));
  const defaults = useMemo(() => getOfferCompensationDefaults(job), [job]);
  const canViewContract =
    compensationSummary?.canViewContract
    ?? (fin.viewContractValue && defaults.contractValue > 0);
  const incentiveTotal = useMemo(
    () =>
      compensationSummary?.incentiveTotal
      ?? (job.offerCompensationConfig?.incentives ?? []).reduce((sum, item) => {
        const amount = Number(item.amount ?? 0);
        return Number.isFinite(amount) && amount > 0 ? sum + Math.round(amount) : sum;
      }, 0),
    [compensationSummary?.incentiveTotal, job.offerCompensationConfig?.incentives],
  );
  const effectiveOfferTotal = compensationSummary?.effectiveOfferTotal ?? (baseline + incentiveTotal);
  const payoutOptionLabel = useMemo(() => {
    if (compensationSummary?.payoutOptionLabel) return compensationSummary.payoutOptionLabel;
    const config = job.offerCompensationConfig;
    if (!config) return "Manual";
    if (config.payoutOption === "revenue_share") {
      return config.revenueShareMode === "percentage"
        ? "Revenue Share · % of Net"
        : "Revenue Share · Fixed Amount";
    }
    if (config.sourceMode === "suggested") return "Suggested";
    if (config.sourceMode === "percent") return "Percent";
    return "Manual";
  }, [compensationSummary?.payoutOptionLabel, job.offerCompensationConfig]);
  const jobRef = displayIdentity(job.jobNumber || job.id, "JOB");

  // ── Expiration state ────────────────────────────────────────────
  const [expiryMode, setExpiryMode] = useState<ExpiryMode>("preset");
  const [presetMinutes, setPresetMinutes] = useState<number>(ttlDefault);
  const [customDateTime, setCustomDateTime] = useState<string>(() =>
    toLocalInputValue(new Date(Date.now() + ttlDefault * 60_000)),
  );

  // Reset on open so each invocation starts fresh.
  useEffect(() => {
    if (open) {
      setExpiryMode("preset");
      setPresetMinutes(ttlDefault);
      setCustomDateTime(
        toLocalInputValue(new Date(Date.now() + ttlDefault * 60_000)),
      );
    }
  }, [open, ttlDefault]);

  // Resolve final TTL.
  const resolvedTtlMinutes = useMemo(() => {
    if (expiryMode === "preset") return presetMinutes;
    const target = new Date(customDateTime);
    if (!Number.isFinite(target.getTime())) return NaN;
    const diff = Math.round((target.getTime() - Date.now()) / 60_000);
    return diff;
  }, [expiryMode, presetMinutes, customDateTime]);

  const ttlValid =
    Number.isFinite(resolvedTtlMinutes) &&
    resolvedTtlMinutes >= 15 &&
    resolvedTtlMinutes <= 60 * 24 * 14;

  const canSend = ttlValid;

  const ttlLabel = ttlValid ? formatTtlLabel(resolvedTtlMinutes) : "—";
  const expiresAtLabel = ttlValid
    ? new Date(Date.now() + resolvedTtlMinutes * 60_000).toLocaleString(
        undefined,
        {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          timeZone: INSTALLER_OFFERS_TIME_ZONE,
        },
      )
    : "invalid";

  // ── Contract-execution age strip ────────────────────────────────
  const contractAge = useMemo(() => {
    if (!contractExecutedAt) return null;
    const t = new Date(contractExecutedAt).getTime();
    if (!Number.isFinite(t)) return null;
    return {
      label: formatAge(contractExecutedAt, new Date()),
      date: new Date(contractExecutedAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: INSTALLER_OFFERS_TIME_ZONE,
      }),
      days: Math.max(0, Math.floor((Date.now() - t) / 86_400_000)),
    };
  }, [contractExecutedAt]);

  const handleConfirm = () => {
    if (!canSend) return;
    onConfirm({
      ttlMinutes: resolvedTtlMinutes,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send Offer</DialogTitle>
          <DialogDescription>
            Confirm installer compensation for this job before dispatch. The
            installer sees only the compensation below — not customer
            contract details.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Recipient + job ref */}
          <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Installer</span>
              <span className="font-medium">{contractor.companyName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Job</span>
              <span className="font-mono">{jobRef}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Scope</span>
              <span
                className="font-medium truncate max-w-[180px] text-right"
                title={job.scopeSummary || job.projectType}
              >
                {job.scopeSummary || job.projectType}
              </span>
            </div>
            {contractAge && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Contract executed</span>
                <span
                  className={`font-medium ${
                    contractAge.days >= 14
                      ? "text-warning"
                      : contractAge.days >= 7
                        ? "text-foreground"
                        : "text-muted-foreground"
                  }`}
                >
                  {contractAge.label} · {contractAge.date}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Offer expires in</span>
              <span className="font-medium">
                {ttlLabel}
                {ttlValid && (
                  <span className="text-muted-foreground font-normal">
                    {" "}· {expiresAtLabel}
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* Compensation summary */}
          <div className="space-y-1.5">
            <Label className="text-xs">
              Offer Compensation
            </Label>
            <div className="rounded-md border bg-muted/40 p-2.5 text-xs space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Base Payout</span>
                <span className="tabular-nums font-medium">
                  ${baseline.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Incentives</span>
                <span className="tabular-nums">
                  {incentiveTotal > 0 ? `+$${incentiveTotal.toLocaleString()}` : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between border-t pt-1 mt-1">
                <span className="text-muted-foreground font-medium">Offer Total</span>
                <span className="tabular-nums font-medium">
                  ${effectiveOfferTotal.toLocaleString()}
                </span>
              </div>
              {canViewContract && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Contract Value</span>
                    <span className="tabular-nums">
                      ${(compensationSummary?.contractValue ?? defaults.contractValue).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      Assumed Finance Fee ({compensationSummary?.financeFeePct ?? defaults.financeFeePct}%
                      {defaults.dealKind === "cash" ? " · cash deal" : ""})
                    </span>
                    <span className="tabular-nums">
                      -${(compensationSummary?.financeFeeAmount ?? defaults.financeFeeAmount).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t pt-1 mt-1">
                    <span className="text-muted-foreground font-medium">Net Contract</span>
                    <span className="tabular-nums font-medium">
                      ${(compensationSummary?.netContract ?? defaults.netContract).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Payout Option</span>
                    <span className="tabular-nums">{payoutOptionLabel}</span>
                  </div>
                </>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Compensation is locked here. Update it in the Offer Compensation Builder before sending.
            </p>
          </div>

          {/* Expiration editor */}
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5">
              <Clock className="h-3 w-3 text-muted-foreground" />
              Offer expiration
            </Label>

            {/* Mode toggle */}
            <div className="inline-flex rounded-md border bg-muted/30 p-0.5 text-[11px]">
              <button
                type="button"
                onClick={() => setExpiryMode("preset")}
                className={`px-2.5 py-1 rounded-sm transition ${
                  expiryMode === "preset"
                    ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Time limit
              </button>
              <button
                type="button"
                onClick={() => setExpiryMode("custom")}
                className={`px-2.5 py-1 rounded-sm transition flex items-center gap-1 ${
                  expiryMode === "custom"
                    ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <CalendarIcon className="h-3 w-3" />
                Specific date / time
              </button>
            </div>

            {expiryMode === "preset" ? (
              <div className="flex flex-wrap gap-1.5">
                {PRESET_TTLS.map((p) => {
                  const active = presetMinutes === p.minutes;
                  return (
                    <button
                      key={p.minutes}
                      type="button"
                      onClick={() => setPresetMinutes(p.minutes)}
                      className={`h-7 px-2.5 rounded-md border text-[11px] transition ${
                        active
                          ? "bg-primary/15 text-primary border-primary/30 ring-1 ring-primary/30"
                          : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <Input
                type="datetime-local"
                value={customDateTime}
                onChange={(e) => setCustomDateTime(e.target.value)}
                className="h-8 text-sm"
                min={toLocalInputValue(new Date(Date.now() + 15 * 60_000))}
                data-testid="offer-expiry-datetime"
              />
            )}

            {!ttlValid && (
              <p className="text-[10px] text-destructive">
                Expiration must be at least 15 minutes from now and no more than 14 days out.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canSend}
            data-testid="offer-confirm-send"
          >
            <Send className="h-3.5 w-3.5 mr-1.5" />
            Send Offer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
